import { isStableUIEnabledForTests } from '@confluence/e2e-url-override/entry-points/isStableUIEnabledForTests.ts';

import type { BannerStateInfo } from './BannerStateContainer.ts';

type Listener = () => void;

/**
 * Module store for the page-top banner stack, read through `useSyncExternalStore`.
 *
 * A module store, not a library: see `docs/llm/agent-instructions/common/state-management.md`.
 *
 * Writes are deferred to a microtask and coalesced here rather than in any caller, so every writer —
 * the container shim and the `useBannerState` hooks — inherits it. Notification is synchronous and
 * several banners still call `show`/`hide` from a render body, so an undeferred write would update
 * other components mid-render.
 *
 * Global and unscoped: only three production containers exist — the client root, the SSR root
 * (mutually exclusive with it), and `WithoutBanners`, which is write-free and read-once.
 *
 * SSR: Tesseract isolates each request, so module state is per-request. `handleReactRender.tsx`
 * serialises the server's list into `window.__SSR_BANNERS__`, which this store reads at module init,
 * so the first render — including the hydrating one — matches the server without waiting on a
 * container. The server has no such global, so its seed is an explicit `seedBanners` call from the
 * SSR entry point; that keeps `ssrBannerManager` out of this module.
 *
 * `stableUI=1` (E2E/VR runs) is honoured here, at all three entry points that can change `banners`,
 * rather than in each writer. `BannerStateContainer` has its own guards, but they only cover the
 * legacy path once this store is the source of truth: `ssrBannerManager` registers banners
 * regardless of the URL param, so the hydration seed would hand the container back the very banners
 * it dropped, and the `useBannerState` hooks that replace it never saw the param at all.
 */

/** Frozen so a consumer cannot mutate it, and stable so `getSnapshot` never triggers a re-render. */
const NO_BANNERS: BannerStateInfo[] = Object.freeze([]) as unknown as BannerStateInfo[];

/**
 * Read at module init rather than from a component, so the store is populated before anything
 * renders. Entries are validated because the value is deserialised from the page.
 *
 * No `typeof window` guard: the build substitutes `globalThis`, so `window` is always defined in both
 * environments. On the server the property simply is not set, which the `Array.isArray` check already
 * covers — the server seed comes from `seedBanners` instead.
 */
const readHydrationBanners = (): BannerStateInfo[] => {
	// `Array.isArray` is deliberately first: it short-circuits on the server, where the global is
	// never set, so the request URI — not available this early in module evaluation — is never read.
	if (!Array.isArray(window.__SSR_BANNERS__) || isStableUIEnabledForTests()) {
		return NO_BANNERS;
	}

	const parsed = window.__SSR_BANNERS__.filter(
		(banner): banner is BannerStateInfo =>
			typeof banner?.name === 'string' && typeof banner?.height === 'number',
	);

	return parsed.length > 0 ? parsed : NO_BANNERS;
};

let banners: BannerStateInfo[] = readHydrationBanners();
const listeners = new Set<Listener>();

/**
 * `useSyncExternalStore` compares snapshots with `Object.is`, so this must return the stored
 * reference rather than build a fresh array. Derived shapes are computed by the reader hooks.
 */
export const getBanners = (): BannerStateInfo[] => banners;

export const subscribeToBanners = (listener: Listener): (() => void) => {
	listeners.add(listener);

	return () => {
		listeners.delete(listener);
	};
};

const commit = (next: BannerStateInfo[]): void => {
	banners = next;
	// Snapshot first: a listener may unsubscribe while being notified.
	Array.from(listeners).forEach((listener) => listener());
};

/** Returns the next list, or `undefined` to signal "nothing changed". */
type Mutation = (current: BannerStateInfo[]) => BannerStateInfo[] | undefined;

let pending: Mutation[] = [];
let pendingFlush: Promise<void> | null = null;

const enqueue = (mutate: Mutation): void => {
	// Evaluated per call rather than memoised at module init, matching `BannerStateContainer.show`:
	// an SPA transition changes `window.location.search`, so the param is not a session constant.
	// Hides are dropped along with shows, which is safe because nothing can enter the store.
	if (isStableUIEnabledForTests()) {
		return;
	}

	pending.push(mutate);

	if (pendingFlush) {
		return;
	}

	pendingFlush = Promise.resolve().then(() => {
		pendingFlush = null;
		const queued = pending;
		pending = [];

		let next = banners;
		let changed = false;

		for (const run of queued) {
			// Applied against the running value, not the pre-flush one, so a name shown twice in a tick
			// resolves against the first write rather than duplicating.
			const result = run(next);
			if (result === undefined) {
				continue;
			}
			next = result;
			changed = true;
		}

		if (changed) {
			commit(next);
		}
	});
};

/**
 * Adds the banner, or updates its height if already present. A no-op when nothing would change, so
 * a redundant call costs no notification. Applied on the next microtask.
 */
export const showBanner = (name: string, height: number): void => {
	enqueue((current) => {
		const index = current.findIndex((banner) => banner.name === name);

		if (index === -1) {
			return [...current, { name, height }];
		}

		if (current[index].height !== height) {
			const next = [...current];
			// Replace rather than mutate, so the previous array keeps the old height.
			next[index] = { name, height };
			return next;
		}

		return undefined;
	});
};

/** Removes the banner if present, otherwise a no-op. Applied on the next microtask. */
export const hideBanner = (name: string): void => {
	enqueue((current) => {
		const index = current.findIndex((banner) => banner.name === name);

		if (index === -1) {
			return undefined;
		}

		const next = [...current];
		next.splice(index, 1);
		return next;
	});
};

/**
 * Server-side seed, and a no-op on the client where `readHydrationBanners` has already run. Only
 * seeds an empty store, so a later container cannot clobber existing state.
 *
 * Deliberately synchronous, unlike `showBanner`/`hideBanner`: the caller mirrors the store into its
 * own state on the very next line, and the SSR render depends on that value being populated before
 * rendering continues.
 */
export const seedBanners = (initial: BannerStateInfo[]): void => {
	if (isStableUIEnabledForTests()) {
		return;
	}

	if (banners.length === 0 && initial.length > 0) {
		commit([...initial]);
	}
};

/** Module state, subscribers and queued writes all outlive a single test — reset them together. */
export const clearBannerStoreForTesting = (): void => {
	banners = NO_BANNERS;
	listeners.clear();
	pending = [];
	pendingFlush = null;
};
